import type { DateRangePickerDynamicDates, DynamicDates, StaticDates } from '@lmn/common-search-widget';
import type { FlexibleCalendarValue } from '@lmn/common-search-widget/components/DateRangePicker/services/types';
import {
  getDateWithoutTime,
  getDateWithoutTimeFromYYYYMMDD,
} from '@/app/components/search/utils/dates/getDateWithoutTime';
import { parse } from 'date-fns';
import { type DateItem, type Dates, DatesType, DateType } from '@/app/components/search/utils';

const getExactDate = (item: DateItem): Date | null =>
  item.type === DateType.EXACT && item.date ? getDateWithoutTime(item.date) : null;

const getCalendar = (departureDate: DateItem, returnDate: DateItem): DynamicDates | StaticDates => ({
  startDate: getExactDate(departureDate),
  endDate: getExactDate(returnDate),
});

const getFlexible = (flexible: DateItem): FlexibleCalendarValue => {
  const { daysFrom, daysTo, months, subType } = flexible;
  let duration;

  if (subType === DateType.WEEKEND) {
    duration = '7';
  } else {
    duration = daysFrom === daysTo ? (daysFrom || '').toString() : `${daysFrom}-${daysTo}`;
  }
  const selectedMonths = months?.map((month) => parse(month, 'yyyy-MM', new Date())) || [];
  return { duration, selectedMonths };
};

/**
 * Adapts a `Dates` object to a `DateRangePickerDynamicDates` object.
 *
 * This function serves as an adapter between the application's `Dates` model
 * and the Common Search Widget's `DateRangePickerDynamicDates` model. It converts
 * the input `Dates` object into a format compatible with the Common Search Widget.
 *
 * @param dates - A `Dates` object from the application.
 * @returns A `DateRangePickerDynamicDates` object compatible with the Common Search Widget,
 *          or `undefined` if the conversion is not possible.
 *
 * @remarks
 * - The function determines the mode ('flexible' or 'calendar') based on the `type` property of the input.
 * - It processes both calendar dates (departure and return) and flexible dates.
 * - The `getCalendar` helper function is used to extract exact dates for calendar mode.
 * - The `getFlexible` helper function is used to process flexible date information.
 *
 * @example
 * const dates = {
 *   type: DatesType.CALENDAR,
 *   departureDate: { type: DateType.EXACT, date: new Date('2023-05-01') },
 *   returnDate: { type: DateType.EXACT, date: new Date('2023-05-10') },
 *   flexDate: { daysFrom: 7, daysTo: 10 }
 * };
 * const cswDates = getCSWDates(dates);
 * // Returns: { mode: 'calendar', calendar: { startDate: Date(...), endDate: Date(...) }, flexible: { duration: '7-10', selectedMonths: [] } }
 */

const getCSWDates = (dates: Dates, departureIntervals?: string): DateRangePickerDynamicDates | undefined => {
  if (!dates) return undefined;
  let calendar;
  const isFlexible = dates.type === DatesType.FLEXIBLE;
  let mode: 'flexible' | 'calendar' = isFlexible ? 'flexible' : 'calendar';

  const flexible = getFlexible(dates.flexDate);

  if (departureIntervals && isFlexible) {
    const [startInterval, endInterval] = departureIntervals.split('-');
    if (startInterval && endInterval) {
      const startDate = getDateWithoutTimeFromYYYYMMDD(startInterval);
      const endDate = getDateWithoutTimeFromYYYYMMDD(endInterval);
      calendar = { startDate, endDate };
      mode = 'calendar';
    }
  } else {
    calendar = getCalendar(dates.departureDate, dates.returnDate);
  }

  return { mode, calendar, flexible };
};

export default getCSWDates;
